import React, { FC, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { t, Trans } from '@lingui/macro'

import ErrorMessage from '../../components/common/error/ErrorMessage'
import HeadTitle from '../../components/common/head/HeadTitle'
import IconButton from '../../components/common/form/button/IconButton'
import IconDoc from '../../components/common/icon/IconDoc'
import IconLink from 'front-end/src/components/common/icon/IconLink'
import IconNote from 'front-end/src/components/common/icon/IconNote'
import IconPeople from '../../components/common/icon/IconPeople'
import IconSettings from '../../components/common/icon/IconSettings'
import PageHeader from '../../components/common/page/header/PageHeader'
import ResourcesSubpage from './resources-subpage/ResourcesSubpage'
import { linkBuilder } from 'shared/linkBuilder'
import { Page } from '../../components/common/page/Page'
import { useWorkspaceBasicInfoQuery } from './ManageWorkspacePage.codegen'
import { WorkspaceBookmarksTab } from './bookmarks-tab/WorkspaceBookmarksTab'
import { WorkspaceDescriptionTab } from './description-tab/WorkspaceDescriptionTab'
import { WorkspaceMembersTab } from './workspace-members-tab/WorkspaceMembersTab'
import { ManageWorkspaceDrawer } from './manage-workspace-drawer/ManageWorkspaceDrawer'
import Loader from 'front-end/src/components/common/loader/Loader'
import { ButtonOutline } from 'front-end/src/components'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import TabsLegacy from 'front-end/src/components/common/tabs/TabsLegacy'

import './ManageWorkspacePage.less'
import IconTag from 'front-end/src/components/common/icon/IconTag'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'

export const ManageWorkspacePage: FC<
  RouteComponentProps<{
    workspaceId: string
  }>
> = ({ match }) => {
  const { userIsAdminOrManager } = usePublicApiSession()
  const workspaceId = parseInt(match.params.workspaceId)
  const [drawerVisible, setDrawerVisible] = useState(false)

  const { data, refetch } = useWorkspaceBasicInfoQuery({
    variables: {
      workspaceId
    }
  })
  if (!data) {
    return <Loader />
  }

  const baseUrl = `/workspace/${workspaceId}/manage`

  const workspace = data.workspace
  if (!workspace) {
    return (
      <ErrorMessage>
        <Trans>Workspace {workspaceId} cannot be found</Trans>
      </ErrorMessage>
    )
  }

  if (workspace?.__typename === 'WorkspaceModelAsMember') {
    return (
      <ErrorMessage>
        <Trans>
          You can not edit workspace {workspaceId}, contact your admin to grant
          you access
        </Trans>
      </ErrorMessage>
    )
  }

  const isCoordinator =
    userIsAdminOrManager ||
    workspace?.__typename === 'WorkspaceModelAsCoordinator'

  const tabList = [
    {
      caption: t`Resources`,
      icon: <IconDoc />,
      href: baseUrl,
      paramName: 'resources',
      getComponent: () => <ResourcesSubpage match={match} />
    }
  ]

  if (isCoordinator) {
    tabList.push({
      caption: t`Access`,
      icon: <IconPeople />,
      href: `${baseUrl}/access`,
      paramName: 'access',
      getComponent: () => <WorkspaceMembersTab workspaceId={workspaceId} />
    })
    tabList.push({
      caption: t`Description`,
      icon: <IconNote />,
      href: `${baseUrl}/description`,
      paramName: 'description',
      getComponent: () => <WorkspaceDescriptionTab workspaceId={workspaceId} />
    })
    tabList.push({
      caption: t`Bookmarks`,
      icon: <IconLink />,
      href: `${baseUrl}/bookmarks`,
      paramName: 'bookmarks',
      getComponent: () => <WorkspaceBookmarksTab workspaceId={workspaceId} />
    })
  }
  return (
    <>
      <HeadTitle title={t`Workspace`} />
      <Page className="ManageWorkspacePage">
        <>
          <PageHeader
            title={workspace?.name || ''}
            className="PageHeader"
            subHeader={
              workspace.tags.length > 0 ? (
                <>
                  <IconTag mr="8px" />
                  {workspace.tags.map((tag) => tag.name).join(', ')}
                </>
              ) : null
            }
            showBackButton={false}
            controls={
              <ButtonOutline
                onClick={() =>
                  navigateTo(linkBuilder.workspaceUrl(workspaceId))
                }
              >
                <Trans>View workspace</Trans>
              </ButtonOutline>
            }
            titleFollowers={
              isCoordinator ? (
                <IconButton
                  hint={t`Settings`}
                  color="medium"
                  onClick={() => {
                    setDrawerVisible(true)
                  }}
                  icon={<IconSettings />}
                />
              ) : undefined
            }
          />

          <TabsLegacy tabList={tabList} />

          {workspace && isCoordinator && (
            <ManageWorkspaceDrawer
              show={drawerVisible}
              workspace={workspace}
              onUpdate={refetch}
              onHide={() => {
                setDrawerVisible(false)
              }}
            />
          )}
        </>
      </Page>
    </>
  )
}
